<script lang="ts">
  import Loading_svelte from "#components/Loading_svelte.svelte";
  import cn from 'classnames'
  import { getContext } from 'svelte'
  import type { Writable } from 'svelte/store'

  import Api from '#js/api'
  import { sgConfirm } from '#js/plugins/SgAlert'
  import { parseDate } from '#js/common/SgDateTime'
  import { ClaimTypes } from '#js/models/CommentClaim'
  import { captureFetchException, getRelativeTimestamp, numString, sgVersion, showModal } from '#js/common/helpers'

  import { showToast } from '#components/toast'
  import SVG from '#components/SVG_svelte.svelte'
  import RatingSpinner from '#components/RatingSpinner_svelte.svelte'
  import type UserModerationDialogType from '#components/user-moderation/Dialog'
  import type CommentMarkAsAnswerModalType from '#components/faq/CommentMarkAsAnswerModal'
  import type CommentModerDeleteFormType from '#components/comments/CommentModerDeleteForm'
  import type { CommentAuthorModel, CommentClaim, CommentModel } from '#components/comments/CommentTypes'

  import CommentLines from './CommentLines.svelte'
  import type CommentFormType from './CommentForm.svelte'
  import type { CommentsStateType } from './MaterialComments.svelte'
  import CommentAttachments from './attachments/CommentAttachments.svelte'

  import css from '#css/comments.pcss.json'
  import formCss from '#css/v9/form.pcss.json'
  import commonCss from '#css/common.pcss.json'

  export let commentId: string | number
  export let showSeparator: boolean = false
  export let hideButtons: boolean = false

  let state: Writable<CommentsStateType> = getContext('comments')
  let comment: CommentModel = $state.comments[commentId]
  let classes: string
  let containerClasses: string[]
  let levels: CommentModel['levels']
  let canEdit: boolean = false
  let canHideChildren: boolean
  let toggleChildrenLevels: CommentModel['levels']
  let formLevel: number
  let formClass: string
  let commentUrl = `#comment_${commentId}`
  let author: CommentAuthorModel
  let userTitle: string
  let timestamp: string
  let parentAuthor: CommentAuthorModel;
  let editTimestamp = ''
  let unprocessedClaims: CommentClaim[]
  let favouriteClasses: string[]
  let favouriteProps: { [key: string]: any }
  let claimProps: { [key: string]: any }
  let hasBeenClaimed: boolean
  let canDelete: boolean
  let CommentForm: typeof CommentFormType

  const date = parseDate(comment.comment_date)
  timestamp = getRelativeTimestamp(date)

  if ($state.comments[commentId].editTimestamp) {
    editTimestamp = getRelativeTimestamp(parseDate($state.comments[commentId].comment_edit_date))
  }

  const commentEditPeriodEnd = (parseDate(comment.comment_date)).getTime() + $state.commentEditTime * 60 * 1000
  const now = (new Date()).getTime()

  canEdit = $state.isModer || (comment.isOwner && commentEditPeriodEnd > now)

  if (canEdit && !$state.isModer) {
    setTimeout(() => {
      canEdit = false
    }, commentEditPeriodEnd - now)
  }

  let isEditMode: boolean = false
  let commentFormShown: boolean = false
  let formLevels = []

  $: {
    commentUrl = `#comment_${commentId}`
    comment = $state.comments[commentId]

    isEditMode = $state.commentEditId === commentId
    commentFormShown = $state.commentFormId === commentId

    classes = cn(css['comment'], {
      [css['comment--highlight']]: comment.highlight === true || comment.isNew
    })
    containerClasses = []
    levels = comment.levels ?? []

    if (levels.length > 0) {
      if (commentFormShown) {
        levels[levels.length - 1].hasChildren = true
      } else {
        levels[levels.length - 1].hasChildren = comment.childrenIds.length > 0
      }
    }

    if (showSeparator && (!comment.level || comment.level == 0)) {
      containerClasses.push(css['comment--separator'])
    }

    if (comment.level > 0) {
      comment.level = comment.level > 6 ? 6 : comment.level
      containerClasses.push(css[`comment--level-${comment.level}`])
    }

    canHideChildren = !$state.threadsExpanded && (comment.level === 2 && comment.totalChildLength > 1)

    toggleChildrenLevels = [...levels, {
      id: -1,
      parentId: comment.comment_id,
      hasLaterSiblings: comment.childrenShown,
      hasChildren: false,
      hideCurrent: comment.childrenShown,
      shortHeight: true
    }]

    formLevels = [
      ...levels,
      {
        id: -1,
        parentId: comment.comment_id,
        hasLaterSiblings: comment.childrenIds.length > 0,
        hasChildren: false,
      }
    ]

    formLevel = comment.level + 1 > 6 ? 6 : comment.level + 1
    formClass = css[`comment--level-${formLevel}`]

    // noinspection TypeScriptValidateTypes
    author = $state.users[comment.user_id]

    if (author) {
      if (author.inTeam) {
        userTitle = 'В команде StopGame'
      } else if (author.isBlogWinner) {
        userTitle = 'Автор блога недели'
      }
    }

    if (comment.sectionData) {
      commentUrl = `${comment.sectionData.url}${commentUrl}`
    }

    if (comment.comment_pid) {
      const parentComment = $state.comments[comment.comment_pid]
      if (parentComment) {
        // noinspection TypeScriptValidateTypes
        parentAuthor = $state.users[parentComment.user_id]
      }
    }

    unprocessedClaims = (comment.claims || []).filter(claim => claim.claim_status === 0)

    favouriteClasses = [css['comment__favourite'], css['button'], css['hide-on-desktop']]
    favouriteProps = {
      'data-plugins': 'auth'
    }

    claimProps = {
      'data-plugins': 'auth'
    }

    if ($state.isLoggedIn) {
      favouriteProps = {
        'data-plugins': 'add-favourite',
        'data-add-favourite-type': 'comments',
        'data-add-favourite-id': comment.comment_id,
        'data-active-class': css['comment__favourite--active']
      }
      claimProps = {}
    }

    if (comment.isFavourite) {
      favouriteClasses.push(css['comment__favourite--active'])
    }

    hasBeenClaimed = $state.isLoggedIn && comment.claims.length > 0 && !!comment.claims.find(claim => claim.isOwner)
    canDelete = $state.isLoggedIn && ($state.isModer || comment.isOwner)

    const commentEditPeriodEnd = (parseDate(comment.comment_date)).getTime() + $state.commentEditTime * 60 * 1000
    const now = (new Date()).getTime()

    canEdit = $state.isModer || (comment.isOwner && commentEditPeriodEnd > now)

    if (canEdit && !$state.isModer) {
      setTimeout(() => {
        canEdit = false
      }, commentEditPeriodEnd - now)
    }

    if ((commentFormShown || isEditMode) && !CommentForm) {
      // noinspection TypeScriptCheckImport
      import(`/dist/js/components/svelte-comments/CommentForm.js?${sgVersion()}`)
        .then(({ default: RealCommentForm }: { default: typeof CommentFormType }) => {
          CommentForm = RealCommentForm
        })
    }
  }

  const claimComment = () => {
    if (!$state.isLoggedIn) {
      return
    }
    // noinspection TypeScriptCheckImport
    import(`/dist/js/components/comments/ClaimForm.js?${sgVersion()}`)
      .then(({ default: ClaimForm }) => {
        // noinspection TypeScriptValidateTypes
        showModal(ClaimForm, {
          // @ts-ignore
          commentId,
          updateComment: $state.updateComment,
        })
      })
  }

  let toggleChildren = () => {
    $state.toggleChildren(commentId)
  }

  const commentLinkClick = (e: MouseEvent) => {
    if ($state.showSectionData) {
      return
    }
    e.preventDefault()
    window.navigator.clipboard.writeText(commentUrl)
      .then(() => {
        showToast({
          type: 'success',
          text: 'Ссылка скопирована в буфер обмена'
        })
      })
  }

  const showParent = () => {
    let commentId = comment.comment_id
    let parentId = comment.comment_pid

    $state.highlightedId = commentId
    $state.highlightedParentId = parentId
    $state.scrollToComment(comment.comment_pid, false, true)
  }

  const returnToReply = () => {
    const replyId = $state.highlightedId

    $state.highlightedId = null
    $state.highlightedParentId = null
    $state.scrollToComment(replyId, false, true)
  }

  const ignoreClaims = () => {
    Api.post({ url: 'comments/ignore-claim', body: { 'id': commentId } })
      .then(result => {
        showToast({
          type: result.status !== 1 ? 'error' : 'success',
          text: result.message
        })

        if (result.status === 1) {
          $state.updateComment(result.comment)
        }
      })
      .catch(e => {
        captureFetchException(e)
        showToast({
          type: 'error',
          text: 'При отклонении жалобы произошла непредвиденная ошибка, попробуй позже'
        })
      })
  }

  const showReplyForm = () => {
    $state.showCommentForm(commentId)
  }

  const editComment = () => {
    $state.showEditForm(commentId)
  }

  const hideCommentForm = () => {
    $state.hideCommentForm()
  }

  const deleteComment = async () => {
    if (!comment.isOwner) {
      // noinspection TypeScriptCheckImport
      import(`/dist/js/components/comments/CommentModerDeleteForm.js?${sgVersion()}`)
        .then(({ default: CommentModerDeleteForm }: {
          default: typeof CommentModerDeleteFormType
        }) => {
          // noinspection TypeScriptValidateTypes
          showModal(CommentModerDeleteForm, {
            comment,
            deleteComment: $state.deleteComment
          })
        })
      return
    }
    if (!await sgConfirm('Точно удалить комментарий?', 'Удалить')) {
      return
    }
    Api.post({ url: 'comments/delete', body: { id: comment.comment_id } })
      .then(result => {
        showToast({
          type: result.status !== 1 ? 'error' : 'success',
          text: result.message
        })

        if (result.status === 1) {
          $state.deleteComment(result.comment.comment_id)
        }
      })
      .catch(error => {
        captureFetchException(error)
        showToast({
          type: 'error',
          text: 'При удалении комментария произошла непредвиденная ошибка, попробуй позже',
        })
      })
  }

  const updateRating = (value: number, voteDirection: '+' | '-' | null) => {
    $state.updateRating(comment.comment_id, value, voteDirection)
  }

  const showMarkAsAnswerModal = () => {
    // noinspection TypeScriptCheckImport
    import(`/dist/js/components/faq/CommentMarkAsAnswerModal.js?${sgVersion()}`)
      .then(({ default: CommentMarkAsAnswerModal }: {
        default: typeof CommentMarkAsAnswerModalType
      }) => {
        // noinspection TypeScriptValidateTypes
        showModal(CommentMarkAsAnswerModal, {
          commentId: comment.comment_id
        })
      })
  }

  const premoderate = () => {
    Api.post({ url: 'comments/approve', body: { id: commentId } })
      .then(result => {
        showToast({
          type: result.status !== 1 ? 'error' : 'success',
          text: result.message
        })

        if (result.status) {
          $state.deleteComment(commentId)
        }
      })
      .catch(e => {
        window.error('Publish Comment', e)
        showToast({
          type: 'error',
          text: 'Не опубликовано: неизвестная ошибка'
        })
      })
  }

  const deleteOnPremoderation = async () => {
    if (!await sgConfirm('Подтверди удаление комментария', 'Удалить')) {
      return
    }
    Api.post({ url: 'comments/delete', body: { 'id': commentId } })
      .then(result => {
        showToast({
          type: result.status !== 1 ? 'error' : 'success',
          text: result.message
        })

        if (result.status === 1) {
          $state.deleteComment(commentId)
          // noinspection TypeScriptCheckImport
          import(`/dist/js/components/user-moderation/Dialog.js?${sgVersion()}`)
            .then(({ default: UserModerationDialog }: {
              default: typeof UserModerationDialogType
            }) => {
              // noinspection TypeScriptValidateTypes
              showModal(UserModerationDialog, {
                userId: comment.user_id,
                reload: false
              })
            })
        }
      })
      .catch((e) => {
        window.error('Delete Comment', e)
        showToast({
          type: 'error',
          text: 'При удалении комментария произошла непредвиденная ошибка, попробуй позже'
        })
      })
  }
</script>

<div class={containerClasses.join(' ')} style="position: relative" id={`comment_${comment.comment_id}`}>
  {#if comment.level > 0}
    <CommentLines levels={levels}/>
  {/if}
  <article class={classes}>
    <section class={css['comment__header']}>
      <a
        class={cn(
          css['comment__author'],
          commonCss['user-info'],
          {
            [commonCss['user-info--in-team']]: author.inTeam,
            [commonCss['user-info--kosar']]: author.isBlogWinner
          }
        )}
        href={author.profileUrl}
        data-user-info
      >
        <picture
          class={cn(commonCss['user-info__avatar'], css['comment__author__avatar'])}
          title={userTitle}
          data-user-info-image
        >
          <source srcset={`${author.avatars['1x-avif']}, ${author.avatars['2x-avif']} 2x`} type='image/avif'/>
          <img
            alt=''
            src={author.avatars['1x-webp']}
            srcset={`${author.avatars['1x-webp']}, ${author.avatars['2x-webp']} 2x`}
            loading="lazy"
          />
        </picture>
        <span class={css['comment__author__name']} data-user-info-name={true}>
          <span class={css['comment__author__name__text']}>
              {#if author.isBanned}
                  <s>{author.userName}</s>
              {:else}
                  {author.userName}
              {/if}
          </span>
          {#if comment.isTopicAuthor}
            <span class={css['topic-starter-icon']} title="Автор материала">А</span>
          {/if}
        </span>
        <span class={css['comment__date']}>{timestamp}</span>
      </a>
      <a
        href={commentUrl}
        on:click={commentLinkClick}
        class={css['comment__link']}
        title={$state.showSectionData ? 'Перейти к комментарию' : 'Ссылка на комментарий'}
      >
        <SVG i='sg-new/link' adaptive/>
      </a>
    </section>
    {#if $state.showSectionData}
      <a class={css['comment__reply-to']} href={comment.sectionData.url}>
        {comment.sectionData.title}
      </a>
    {:else if comment.comment_pid && parentAuthor}
      <div
        role="button"
        tabindex="0"
        on:click={showParent}
        class={css['comment__reply-to']}
      >
        Ответ на комментарий {parentAuthor.userName}
      </div>
    {/if}
    {#if isEditMode}
      {#if CommentForm}
        <CommentForm
          pid={comment.comment_id}
          shown
          open
          action="edit"
          comment={comment}
          onFormSubmit={hideCommentForm}
          targetType={comment.target_type}
          targetId={comment.target_id}
          showCancel
        />
      {:else}
        <Loading_svelte string=""/>
      {/if}
    {:else}
      {#if comment.comment_delete}
        <div class="system-message system-message-alert">
          Этот комментарий уже был удалён и выводится здесь для контекста
        </div>
      {/if}
      <div class={css['comment__body']}>
        {@html comment.text}
      </div>
      {#if comment.editTimestamp}
        <p class={css['comment__edit-info']}>
          Отредактировано{comment.editedByModer ? ' модератором' : ''} {editTimestamp}
        </p>
      {/if}
      {#if comment.attachments?.length > 0}
        <CommentAttachments attachments={comment.attachments} commentId={comment.comment_id}/>
      {/if}
      {#if unprocessedClaims.length > 0 && $state.isModer}
        <div class="system-message system-message-alert">
          Жалоба от:<br/>
          {#each unprocessedClaims as claim (`${claim.comment_id}_${claim.user_id}`)}
            <p>
              <a href={claim.user.profileUrl}>
                {claim.user.userName}
              </a>, {claim.timestamp}, причина: {ClaimTypes[claim.claim_type]}
            </p>
          {/each}
          <div style="display: flex; column-gap: 16px;">
            <div
              role="button"
              tabindex="0"
              class='system-message__button'
              on:click={ignoreClaims}
            >
              Игнорировать
            </div>
            <div
              role="button"
              tabindex="0"
              class='system-message__button'
              on:click={deleteComment}
            >
              Удалить
            </div>
          </div>
        </div>
      {/if}
      {#if comment.comment_spam && !comment.comment_publish && $state.isModer}
        <div class="system-message system-message-alert">
          Комментарий на премодерации
          <div style="display: flex; column-gap: 16px; margin-top: 16px">
            <div
              role="button"
              tabindex="0"
              class='system-message__button'
              on:click={premoderate}
            >
              Опубликовать
            </div>
            <div
              role="button"
              tabindex="0"
              class='system-message__button'
              on:click={deleteOnPremoderation}
            >
              Удалить
            </div>
          </div>
        </div>
      {/if}
      {#if $state.canHaveAnswer}
        {#if $state.answerId === null && $state.canChooseAnswer}
          <label
            class={formCss['checkbox__container']}
            style="column-gap: 8px; margin-top: 8px;"
            on:click={showMarkAsAnswerModal}
          >
            <!--suppress HtmlUnknownTag -->
            <div class={formCss['checkbox__checkbox']}></div>
            <span class={formCss['checkbox__text']} style="font-size: 16px">Отметить как решение</span>
          </label>
        {:else if $state.answerId === comment.comment_id}
          <div class={css['comment__is-answer']}>
            <SVG i='sg-new/check' adaptive/>
            Решение вопроса
          </div>
        {/if}
      {/if}
      {#if $state.highlightedParentId === comment.comment_id && $state.highlightedId}
        <div
          role="button"
          tabindex="0"
          on:click={returnToReply}
          class={css['comment__reply-to']}
        >
          ↓ Вернуться к ответу
        </div>
      {/if}
      {#if !hideButtons}
        <div class={css['comment__footer']}>
          {#if !$state.commentFormDisabled}
            <div
              role="button"
              tabindex="0"
              class={css['button']}
              on:click={showReplyForm}
            >
              <SVG i="sg-new/reply" adaptive/>
              <span>Ответить</span>
            </div>
          {/if}
          <div
            role="button"
            tabindex="0"
            class={favouriteClasses.join(' ')}
            title="В закладки"
            {...favouriteProps}
          >
            <SVG i="sg-new/bookmark" adaptive/>
          </div>
          {#if !comment.isOwner && !hasBeenClaimed}
            <div
              role="button"
              tabindex="0"
              class={[css['button'], css['hide-on-desktop']].join(' ')}
              title="Пожаловаться"
              on:click={claimComment}
              {...claimProps}
            >
              <SVG i="sg-new/warning" adaptive/>
            </div>
          {/if}
          {#if canEdit}
            <div
              role="button"
              tabindex="0"
              class={[css['button'], css['hide-on-desktop']].join(' ')}
              title="Редактировать"
              on:click={editComment}
            >
              <SVG i="sg-new/edit" adaptive/>
            </div>
          {/if}
          {#if canDelete}
            <div
              role="button"
              tabindex="0"
              class={[css['button'], css['hide-on-desktop']].join(' ')}
              title="Удалить"
              on:click={deleteComment}
            >
              <SVG i="sg-new/trash" adaptive/>
            </div>
          {/if}
          <div class={css['right']}>
            <RatingSpinner
              targetType="comment"
              targetId={comment.comment_id}
              value={comment.comment_rating}
              vote={comment.currentUserVote}
              isGuest={!$state.isLoggedIn}
              canVote={!comment.isOwner}
              updateRating={updateRating}
              showDisabledButtons
            />
          </div>
        </div>
      {/if}
    {/if}
  </article>
</div>
{#if commentFormShown && CommentForm}
  <div class={formClass}>
    <CommentLines levels={formLevels}/>
    <CommentForm
      pid={comment.comment_id}
      onFormSubmit={hideCommentForm}
      targetType={comment.target_type}
      targetId={comment.target_id}
      open
      shown
      showCancel
    />
  </div>
{/if}
{#if canHideChildren}
  <div class={formClass}>
    <CommentLines levels={toggleChildrenLevels}/>
    <div
      role="button"
      tabindex="0"
      class={css['comment__hide-children']}
      on:click={toggleChildren}
    >
      Ещё {comment.totalChildLength} {numString(comment.totalChildLength, ['комментарий', 'комментария', 'комментариев'])}
      {#if comment.childrenShown}
        <SVG i='sg/chevron-up'/>
      {:else}
        <SVG i='sg/chevron-down'/>
      {/if}
    </div>
  </div>
{/if}
